import { Link } from "react-router-dom";
import { ShieldCheck, Users, Heart, Sparkles, MapPin, BadgeCheck, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { WhyChooseUs } from "@/components/home/WhyChooseUs";
import { CtaBand } from "@/components/CtaBand";
import { container, sectionPad, label, btnPrimary, cardShadow } from "@/lib/ui";
import { BUSINESS, IMAGES } from "@/data/business";
import { localBusinessSchema, breadcrumbSchema } from "@/lib/schema";

const VALUES = [
  { icon: Heart, title: "Honest & Local", desc: "A Melbourne-based sole trader who genuinely cares about doing right by every home." },
  { icon: Sparkles, title: "Detail-Focused", desc: "We treat your home with care and never cut corners." },
  { icon: Users, title: "Friendly & Approachable", desc: "A team you'll feel comfortable having in your home." },
  { icon: ShieldCheck, title: "Trusted & Insured", desc: "ABN registered, police checked and covered by Public Liability Insurance." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Us — Melbourne House Cleaners"
        description="JK House Cleaning is a local, ABN-registered residential cleaning business serving Melbourne. Friendly, reliable, insured and police checked. Get to know us."
        path="/about"
        schema={[localBusinessSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])]}
      />
      <PageHeader
        eyebrow="About Us"
        title="A local Melbourne cleaning business you can rely on"
        subtitle="We help home owners, families, busy professionals, tenants and hosts across Melbourne enjoy cleaner, fresher homes — without the stress."
        crumbs={[{ name: "About" }]}
      />

      <section className={`${sectionPad} bg-white`}>
        <div className={`${container} grid lg:grid-cols-2 gap-12 items-center`}>
          <div className="rounded-3xl overflow-hidden shadow-xl">
            <img src={IMAGES.cleaner} alt="Professional cleaner at work in a Melbourne home" className="w-full h-[440px] object-cover" loading="lazy" />
          </div>
          <div>
            <span className={label}>Who We Are</span>
            <h2 className="mt-3 font-heading text-3xl font-bold text-navy tracking-tight">
              Professional residential cleaning, done properly
            </h2>
            <div className="mt-5 space-y-4 text-gray-600 leading-relaxed">
              <p>
                {BUSINESS.name} is a proudly local, ABN-registered residential cleaning business based in
                Melbourne. We focus entirely on homes — houses, apartments, units and townhouses — delivering
                a reliable, detail-focused clean every time.
              </p>
              <p>
                We bring our own professional equipment and cleaning products, work around a schedule that
                suits your household, and always provide a free, personalised quote before we start.
              </p>
              <p>
                Whether you need a regular clean, a one-off refresh, a deep clean or an end-of-lease service,
                our goal is simple: a spotless home and complete peace of mind.
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-neutralbg px-4 py-2 text-sm font-medium text-navy">
                <BadgeCheck className="w-4 h-4 text-brandgreen" /> ABN {BUSINESS.abn}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-neutralbg px-4 py-2 text-sm font-medium text-navy">
                <MapPin className="w-4 h-4 text-brandgreen" /> {BUSINESS.region}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-4 bg-white">
        <div className={container}>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className={`rounded-2xl bg-neutralbg/70 border border-gray-100 p-6 ${cardShadow}`}>
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-navy">
                    <Icon className="w-6 h-6 text-brandgreen-light" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{v.title}</h3>
                  <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <WhyChooseUs />

      <section className="pb-20 bg-white">
        <div className={container}>
          <div className={`rounded-3xl bg-neutralbg/70 border border-gray-100 p-8 text-center ${cardShadow}`}>
            <p className="text-sm text-gray-500">
              Our logo is a clean placeholder and can be replaced by the business owner at any time.
            </p>
            <Link to="/quote" className={`${btnPrimary} mt-5`} data-testid="about-quote">
              Get your free quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
